import type { LeaveRequestListItem } from "@/modules/hr/leave/domain/types";
import {
    Badge,
    Button,
    Checkbox,
    createSortableColumn,
    DataTable,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    type TanstackReactTable,
} from "@devhop/ui";
import {
    Check,
    Eye,
    MoreVertical,
    Pencil,
    Trash2,
    XCircle,
} from "lucide-react";

const STATUS_LABEL: Record<LeaveRequestListItem["status"], string> = {
  pending_line: "ລໍຖ້າຫົວໜ້າສາຍ",
  pending_hr: "ລໍຖ້າ HR",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

type LeaveApprovalListTableProps = {
  data: LeaveRequestListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onApprove: (row: LeaveRequestListItem) => void;
  onReject: (row: LeaveRequestListItem) => void;
  onViewDetail?: (row: LeaveRequestListItem) => void;
  onEdit?: (row: LeaveRequestListItem) => void;
  onDelete?: (row: LeaveRequestListItem) => void;
  /** When false, hide row actions (e.g. on history page). */
  showActions?: boolean;
  /** ອະນຸມັດ/ປະຕິເສດໃນແຖວ: ສະເພາະ HR (ອະນຸມັດຕອນ pending_hr; ປະຕິເສດເມື່ອ pending_line ຫຼື pending_hr). */
  canApproveHr?: boolean;
  /** Show bulk ອະນຸມັດ (ສັງເກດກັດກັ້ນຂອງ backend ສະເພາະ pending_hr). */
  canBulkApprove?: boolean;
  /** Bulk: allow same status; backend applies to pending only where applicable. */
  selectedIds?: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
  onBulkApprove?: (ids: string[]) => void;
  onBulkReject?: (ids: string[]) => void;
  onBulkDelete?: (ids: string[]) => void;
};

export function LeaveApprovalListTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onApprove,
  onReject,
  onViewDetail,
  onEdit,
  onDelete,
  showActions = true,
  selectedIds = [],
  onSelectedIdsChange,
  onBulkApprove,
  onBulkReject,
  onBulkDelete,
  canApproveHr = false,
  canBulkApprove = false,
}: LeaveApprovalListTableProps) {
  const enableSelection = onSelectedIdsChange != null;
  const selectableIds = data.map((r) => r.id);
  const allPageSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));
  const somePageSelected = selectableIds.some((id) => selectedIds.includes(id));
  const setSelection = (ids: string[]) => onSelectedIdsChange?.(ids);
  const toggleRow = (id: string) => {
    if (selectedIds.includes(id))
      setSelection(selectedIds.filter((x) => x !== id));
    else setSelection([...selectedIds, id]);
  };
  const toggleAll = () => {
    if (allPageSelected)
      setSelection(selectedIds.filter((id) => !selectableIds.includes(id)));
    else setSelection([...new Set([...selectedIds, ...selectableIds])]);
  };

  const columns: TanstackReactTable.ColumnDef<LeaveRequestListItem>[] = [
    ...(enableSelection
      ? [
          {
            id: "select",
            header: () => (
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-input/60 bg-muted/40">
                <Checkbox
                  checked={
                    selectableIds.length === 0
                      ? false
                      : allPageSelected
                        ? true
                        : somePageSelected
                          ? "indeterminate"
                          : false
                  }
                  onCheckedChange={
                    selectableIds.length === 0 ? undefined : toggleAll
                  }
                  disabled={selectableIds.length === 0}
                  aria-label="ເລືອກທັງໝົດ"
                  className="size-4"
                />
              </div>
            ),
            cell: ({ row }) => (
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-input/60 bg-muted/40">
                <Checkbox
                  checked={selectedIds.includes(row.original.id)}
                  onCheckedChange={() => toggleRow(row.original.id)}
                  aria-label={`ເລືອກ ${row.original.employeeName}`}
                  className="size-4"
                />
              </div>
            ),
            size: 44,
            enableSorting: false,
          } as TanstackReactTable.ColumnDef<LeaveRequestListItem>,
        ]
      : []),
    createSortableColumn<LeaveRequestListItem>("employeeName", "ພະນັກງານ", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.employeeName}</div>
          <div className="text-muted-foreground text-xs">
            {row.original.employeeEmail}
          </div>
        </div>
      ),
    }),
    createSortableColumn<LeaveRequestListItem>("startDate", "ວັນເລີ່ມ – ສິ້ນສຸດ", {
      size: 180,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.startDate)} –{" "}
          {formatDate(row.original.endDate)}
        </span>
      ),
    }),
    createSortableColumn<LeaveRequestListItem>("leaveTypeName", "ປະເພດລາ", {
      size: 140,
      cell: ({ row }) => (
        <span>
          {row.original.leaveTypeName}
          {row.original.leaveTypeCode ? ` (${row.original.leaveTypeCode})` : ""}
        </span>
      ),
    }),
    createSortableColumn<LeaveRequestListItem>("days", "ວັນລາ", {
      size: 80,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{row.original.days} ວັນ</span>
      ),
    }),
    {
      id: "remainingDays",
      header: "ສິດຄົງເຫຼືອ",
      size: 100,
      cell: ({ row }) => {
        const r = row.original.remainingDays;
        if (r == null) return <span className="text-muted-foreground">—</span>;
        return <span className="tabular-nums">{r} ວັນ</span>;
      },
      enableSorting: false,
    },
    createSortableColumn<LeaveRequestListItem>("status", "ສະຖານະ", {
      size: 120,
      cell: ({ row }) => {
        const s = row.original.status;
        const variant =
          s === "approved"
            ? "default"
            : s === "rejected"
              ? "destructive"
              : "secondary";
        return <Badge variant={variant}>{STATUS_LABEL[s]}</Badge>;
      },
    }),
    ...(showActions || onViewDetail
      ? [
          {
            id: "actions",
            header: "ການດຳເນີນການ",
            size: 280,
            cell: ({
              row,
            }: {
              row: TanstackReactTable.Row<LeaveRequestListItem>;
            }) => {
              const r = row.original;
              const showApprove =
                showActions &&
                canApproveHr &&
                r.status === "pending_hr";
              const showReject =
                showActions &&
                canApproveHr &&
                (r.status === "pending_line" ||
                  r.status === "pending_hr");
              const allowEditDeletePending =
                showActions &&
                canApproveHr &&
                (r.status === "pending_line" || r.status === "pending_hr");
              return (
                <div className="flex flex-wrap items-center gap-2">
                  {showApprove && (
                    <Button
                      size="sm"
                      variant="default"
                      className="h-7 gap-1"
                      onClick={() => onApprove(r)}
                      aria-label={`ອະນຸມັດ ${r.employeeName}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                      ອະນຸມັດ
                    </Button>
                  )}
                  {showReject && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                      onClick={() => onReject(r)}
                      aria-label={`ປະຕິເສດ ${r.employeeName}`}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      ປະຕິເສດ
                    </Button>
                  )}
                  {onViewDetail && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                      onClick={() => onViewDetail(r)}
                      aria-label={`ເບິ່ງລາຍລະອຽດ ${r.employeeName}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      ລາຍລະອຽດ
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="size-8 p-0"
                        aria-label="ເມນູ"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {allowEditDeletePending && onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(r)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          ແກ້ໄຂ
                        </DropdownMenuItem>
                      )}
                      {allowEditDeletePending && onDelete && (
                        <DropdownMenuItem
                          onClick={() => onDelete(r)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          ລຶບ
                        </DropdownMenuItem>
                      )}
                      {/* {showActions && isPending && (
                        <>
                          <DropdownMenuItem onClick={() => onApprove(r)}>
                            <Check className="mr-2 h-4 w-4" />
                            ອະນຸມັດ
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onReject(r)}>
                            <XCircle className="mr-2 h-4 w-4" />
                            ປະຕິເສດ
                          </DropdownMenuItem>
                        </>
                      )} */}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            },
            enableSorting: false,
          } as TanstackReactTable.ColumnDef<LeaveRequestListItem>,
        ]
      : []),
  ];

  return (
    <>
      {enableSelection && selectedIds.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-sm">
            ເລືອກ {selectedIds.length} ລາຍການ
          </span>
          {onBulkApprove && canBulkApprove && (
            <Button size="sm" onClick={() => onBulkApprove(selectedIds)}>
              ອະນຸມັດທີ່ເລືອກ
            </Button>
          )}
          {onBulkReject && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onBulkReject(selectedIds)}
            >
              ປະຕິເສດທີ່ເລືອກ
            </Button>
          )}
          {onBulkDelete && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onBulkDelete(selectedIds)}
            >
              ລຶບທີ່ເລືອກ
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelection([])}>
            ລ້າງການເລືອກ
          </Button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        totalCount={totalCount}
        offset={offset}
        limit={limit}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          const s = sorting[0];
          if (s?.id) onSortingChange(s.id, !!s.desc);
        }}
      />
    </>
  );
}
